import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

interface LinhaMovel {
  telefone?: string;
  plano?: string;
  fatura_media?: number;
  gb_contratado?: number;
  gb_trafegado?: number;
  aparelho?: string;
  m?: number | null;
  fidelizado?: string;
}

interface ContaFixa {
  conta?: string;
  designador?: string;
  produto?: string;
  tipo_produto?: string;
  tecnologia?: string;
  valor?: number;
  m?: number | null;
  endereco?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
}

const formatarTelefone = (telefone: string | undefined): string => {
  if (!telefone) return '-';
  const digits = telefone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return telefone;
};

export function exportarLinhasMovelExcel(
  linhasMovel: LinhaMovel[],
  contasFixo: ContaFixa[],
  cnpj: string,
  razaoSocial?: string
): void {
  const workbook = XLSX.utils.book_new();
  const dataExport = format(new Date(), 'dd-MM-yyyy_HH-mm');

  // Aba 1: Linhas Móveis
  if (linhasMovel.length > 0) {
    const dadosMovel = linhasMovel.map((linha, idx) => ({
      '#': idx + 1,
      'Telefone': formatarTelefone(linha.telefone),
      'Plano': linha.plano || '-',
      'Fatura Média (R$)': linha.fatura_media ?? 0,
      'GB Contratado': linha.gb_contratado ?? 0,
      'GB Trafegado': linha.gb_trafegado ?? 0,
      'Aparelho': linha.aparelho || '-',
      'M (Meses)': linha.m ?? 'N/A',
      'Fidelizado': linha.fidelizado || 'N/I',
      'Aptidão': linha.m !== null && linha.m !== undefined && linha.m >= 24 ? 'SEM MULTA' : 'COM MULTA',
    }));

    // Adicionar totais
    const totalFatura = linhasMovel.reduce((acc, l) => acc + (l.fatura_media || 0), 0);
    const totalGBContratado = linhasMovel.reduce((acc, l) => acc + (l.gb_contratado || 0), 0);
    const totalGBTrafegado = linhasMovel.reduce((acc, l) => acc + (l.gb_trafegado || 0), 0);
    
    dadosMovel.push({
      '#': '' as any,
      'Telefone': 'TOTAL',
      'Plano': `${linhasMovel.length} linhas`,
      'Fatura Média (R$)': totalFatura,
      'GB Contratado': totalGBContratado,
      'GB Trafegado': totalGBTrafegado,
      'Aparelho': '',
      'M (Meses)': '' as any,
      'Fidelizado': '',
      'Aptidão': '',
    });

    const wsMovel = XLSX.utils.json_to_sheet(dadosMovel);
    
    // Configurar largura das colunas
    wsMovel['!cols'] = [
      { wch: 4 },   // #
      { wch: 16 },  // Telefone
      { wch: 30 },  // Plano
      { wch: 15 },  // Fatura
      { wch: 12 },  // GB Contratado
      { wch: 12 },  // GB Trafegado
      { wch: 25 },  // Aparelho
      { wch: 10 },  // M
      { wch: 12 },  // Fidelizado
      { wch: 12 },  // Aptidão
    ];
    
    XLSX.utils.book_append_sheet(workbook, wsMovel, 'Linhas Móveis');
  }

  // Aba 2: Contas Fixas
  if (contasFixo.length > 0) {
    const dadosFixo = contasFixo.map((conta, idx) => ({
      '#': idx + 1,
      'Conta': conta.conta || '-',
      'Designador': conta.designador || '-',
      'Produto': conta.produto || '-',
      'Tipo': conta.tipo_produto || '-',
      'Tecnologia': conta.tecnologia || '-',
      'Valor (R$)': conta.valor ?? 0,
      'M (Meses)': conta.m ?? 'N/A',
      'Endereço': conta.endereco || '-',
      'Bairro': conta.bairro || '-',
      'Município': conta.municipio || '-',
      'UF': conta.uf || '-',
      'CEP': conta.cep || '-',
    }));

    // Adicionar totais
    const totalValor = contasFixo.reduce((acc, c) => acc + (c.valor || 0), 0);
    
    dadosFixo.push({
      '#': '' as any,
      'Conta': 'TOTAL',
      'Designador': `${contasFixo.length} contas`,
      'Produto': '',
      'Tipo': '',
      'Tecnologia': '',
      'Valor (R$)': totalValor,
      'M (Meses)': '' as any,
      'Endereço': '',
      'Bairro': '',
      'Município': '',
      'UF': '',
      'CEP': '',
    });

    const wsFixo = XLSX.utils.json_to_sheet(dadosFixo);
    
    // Configurar largura das colunas
    wsFixo['!cols'] = [
      { wch: 4 },   // #
      { wch: 15 },  // Conta
      { wch: 20 },  // Designador
      { wch: 30 },  // Produto
      { wch: 20 },  // Tipo
      { wch: 12 },  // Tecnologia
      { wch: 12 },  // Valor
      { wch: 10 },  // M
      { wch: 30 },  // Endereço
      { wch: 15 },  // Bairro
      { wch: 15 },  // Município
      { wch: 5 },   // UF
      { wch: 10 },  // CEP
    ];
    
    XLSX.utils.book_append_sheet(workbook, wsFixo, 'Contas Fixas');
  }

  // Se não houver nenhuma aba, criar uma vazia
  if (linhasMovel.length === 0 && contasFixo.length === 0) {
    const wsVazia = XLSX.utils.json_to_sheet([{ 'Mensagem': 'Nenhuma linha importada para este lead' }]);
    XLSX.utils.book_append_sheet(workbook, wsVazia, 'Sem Dados');
  }

  // Gerar arquivo
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  const nomeArquivo = `base-linhas-${cnpjLimpo}-${dataExport}.xlsx`;
  
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  saveAs(blob, nomeArquivo);
}
